import React, { useContext, useState } from 'react'
import { GridContainer, GridItem } from '@entur/grid'
import { Heading3, Label, Paragraph, SmallText } from '@entur/typography'
import { Tag } from '@entur/layout'
import { PrimaryButton } from '@entur/button'
import { colors, space } from '@entur/tokens'
import { BannerAlertBox, useToast } from '@entur/alert'
import { FeedbackText } from '@entur/form'
import { useEmployee } from '../../../contexts/employee-context'
import { useSyncTicketRights } from '../../../hooks/use-ticket-rights'
import { FetchState, TicketRight } from '../../../api-clients/api-types'
import { InfoCard } from '../../card/card'
import { useFamilyMembers } from '../../../contexts/family-members-context'
import { toPrintFormat } from '../../../utils/date-utils'
import { DisabledContext } from '../../../contexts/disable-context'
import { findTicketRightsToSync } from '../../../utils/ticket-rights-utils'
import { RuleElement } from '../rule-element'
import { useClientInfo } from '../../../contexts/client-info-context'
import { ReadOnlyContext } from '../../../contexts/read-only-context'

type TicketRightSyncProps = {
  ticketRight: TicketRight
  surname: string
  firstName: string
}
export const TicketRightSync: React.FC<TicketRightSyncProps> = ({ ticketRight, surname, firstName }) => {
  const { hasSensitiveAccess } = useClientInfo()
  const { employee } = useEmployee()
  const typeOfChange = ticketRight.note === 'OPPHØR' ? ticketRight.note : 'ENDRING'
  const { disabled } = useContext(DisabledContext)
  const { readOnly } = useContext(ReadOnlyContext)
  const { addToast } = useToast()
  const [action, setAction] = useState('')
  const [syncTicketRightResponse, syncTicketRight] = useSyncTicketRights(() => {
    addToast('Rettighet er oppdatert!')
  })
  const isAutoSync = ticketRight.essSyncStatus?.isReadyToSync

  const isSyncDisabled =
    hasSensitiveAccess === false || syncTicketRightResponse.status === FetchState.LOADING || readOnly

  const top = (
    <div>
      <Tag>{typeOfChange}</Tag>{' '}
      <SmallText style={{ color: colors.blues.blue40, marginLeft: space.extraSmall }}>
        Opprettet: {toPrintFormat(ticketRight.calculationTimestamp)}
      </SmallText>{' '}
      <PrimaryButton
        disabled={isSyncDisabled}
        onClick={() => {
          syncTicketRight(ticketRight, true)
          setAction('Synkronisering')
        }}
        style={{ float: 'right', marginRight: space.medium, width: '15.75rem' }}
      >
        Synkroniser rettighet
      </PrimaryButton>
      {syncTicketRightResponse.status === FetchState.ERROR && (
        <BannerAlertBox
          variant="error"
          title={`${action} feilet`}
          style={{ marginBottom: space.medium, marginTop: space.large }}
        >
          Det har skjedd en teknisk feil under synkroniseringen
        </BannerAlertBox>
      )}
      {syncTicketRightResponse.status === FetchState.SUCCESS &&
        syncTicketRightResponse.data.essSyncStatus?.errorMessage !== null && (
          <BannerAlertBox
            variant="error"
            title="Synkronisering til ESS feilet"
            style={{ marginBottom: space.medium, marginTop: space.large }}
          >
            {syncTicketRightResponse.data.essSyncStatus?.errorMessage}
          </BannerAlertBox>
        )}
    </div>
  )

  if (employee.draftEmployee) {
    return <></>
  }

  return (
    <InfoCard
      className={`ticket-right-sync-card ${disabled ? 'disabled' : ''}`}
      key={ticketRight.ticketrightId}
      header={<Heading3> Ny rettighet er ikke synkronisert</Heading3>}
      top={top}
      style={{ marginBottom: space.medium }}
    >
      <GridContainer spacing="small">
        <GridItem medium={2}>
          <Label>Rettighetshaver</Label>
          <Paragraph style={{ marginBottom: 0 }}>
            {surname}, {firstName}{' '}
          </Paragraph>
        </GridItem>
        <GridItem medium={3}>
          <Label>Periode</Label>
          <Paragraph style={{ marginBottom: 0 }}>
            {toPrintFormat(ticketRight.validFrom)} - {toPrintFormat(ticketRight.validUntil)}
          </Paragraph>
        </GridItem>
        <GridItem medium={2}>
          <Label>Produkt</Label>
          <Paragraph style={{ marginBottom: 0 }}>{ticketRight.ticketType.code}</Paragraph>
        </GridItem>
        <GridItem medium={3}>
          <RuleElement createdByRule={ticketRight?.createdByRule} />
        </GridItem>
        <GridItem medium={12}>
          {isAutoSync && <FeedbackText variant="info">Rettigheten synkroniseres automatisk</FeedbackText>}
        </GridItem>
      </GridContainer>
    </InfoCard>
  )
}

export const EmployeeTicketRightsSyncCards = () => {
  const { employee } = useEmployee()

  return (
    <>
      {findTicketRightsToSync(employee.ticketRights)?.map((ticketRight) => (
        <TicketRightSync
          ticketRight={ticketRight}
          firstName={employee.firstName}
          surname={employee.surname}
          key={ticketRight.ticketrightId}
        />
      ))}
    </>
  )
}

export const FamilyMembersTicketRightsSyncCards = () => {
  const { familyMembersResponse } = useFamilyMembers()

  if (familyMembersResponse.status === FetchState.SUCCESS) {
    return (
      <div style={{ marginBottom: space.extraLarge2 }}>
        {familyMembersResponse.data
          .flatMap((it) =>
            findTicketRightsToSync(it.ticketRights).map((ticketRight) => {
              return {
                firstName: it.firstName,
                surname: it.surname,
                ticketRight,
              }
            })
          )
          .map((it) => {
            if (it?.ticketRight) {
              return (
                <TicketRightSync
                  ticketRight={it.ticketRight}
                  firstName={it.firstName}
                  surname={it.surname}
                  key={it.ticketRight.ticketrightId}
                />
              )
            }
            return <></>
          })}
      </div>
    )
  }
  return <></>
}
